import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import TaskItem from '@/components/v2/molecules/admin/tabs/AdminTodosTab/TaskItem';
import {
  EnrichedTask,
  useUpdateTask,
} from '@/hooks/admin/AdminTodosTabs/useTasks';
import { cn } from '@/lib/utils';
import { parseDueDate, toISODate } from '@/utils/shared/dateUtils';
import { Plus } from 'lucide-react';
import { useState } from 'react';

interface TaskColumnProps {
  title: string;
  subtitle: string;
  tasks: EnrichedTask[];
  onTaskClick: (task: EnrichedTask) => void;
  onCreateTaskClick: (dueDate: Date | null) => void;
  columnType: string;
}

const TaskColumn = ({
  title,
  subtitle,
  tasks,
  onTaskClick,
  onCreateTaskClick,
  columnType,
}: TaskColumnProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const updateTask = useUpdateTask();

  const completedTasks = tasks.filter((task) => task.status === 'completed');
  const pendingTasks = tasks.filter((task) => task.status === 'pending');

  const sortedTasks = [
    ...pendingTasks.sort((a, b) => a.sort_order - b.sort_order),
    ...completedTasks.sort((a, b) => a.sort_order - b.sort_order),
  ];

  const getDueDate = (): Date | null => {
    if (columnType === 'backlog') return null;

    try {
      return parseDueDate(columnType);
    } catch (error) {
      console.error('Invalid columnType date:', columnType);
      return null;
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const taskData = JSON.parse(e.dataTransfer.getData('application/json'));
      const newDueDate = getDueDate();
      const newDateString = newDueDate ? toISODate(newDueDate) : null;

      if (taskData.due_date !== newDateString) {
        updateTask.mutate(
          {
            id: taskData.id,
            updates: {
              due_date: newDateString,
            },
          },
          {
            onSuccess: (data) => {
              console.log('Task updated successfully:', data);
            },
            onError: (error) => {
              console.error('Error updating task:', error);
            },
          },
        );
      } else {
        console.log('Dates are the same, no update needed');
      }
    } catch (error) {
      console.error('Error dropping task:', error);
    }
  };

  return (
    <div
      className={cn(
        'bg-white flex flex-col h-full lg:min-h-0 space-y-3 transition-all duration-200 border rounded-lg p-4',
        isDragOver && 'bg-muted/20 p-2',
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between px-1">
        <div>
          <h3 className="text-lg font-semibold capitalize">{title}</h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>

        <Badge variant="secondary" className="shrink-0">
          {tasks.length}
        </Badge>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={() => onCreateTaskClick(getDueDate())}
        className="w-full justify-start gap-2 h-8 text-xs border-dashed"
      >
        <Plus className="h-3 w-3" />
        Nueva tarea
      </Button>

      <div
        className={cn(
          'flex-1 min-h-0 overflow-y-auto custom-scrollbar-vertical space-y-2 transition-all duration-200 rounded-lg',
          isDragOver &&
            'border-2 border-dashed border-primary bg-primary/5 p-2',
        )}
      >
        {sortedTasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {isDragOver ? 'Suelta la tarea aquí' : 'No hay tareas'}
          </div>
        ) : (
          sortedTasks.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              onClick={() => onTaskClick(task)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default TaskColumn;
